import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import api, { isDriveEmployee } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import DriveExplorer from "@/components/DriveExplorer";
import SharedLinks from "@/components/SharedLinks";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { UserAvatar } from "@/components/Avatar";
import { ArrowLeft, HardDrive, ShieldAlert } from "lucide-react";
import { useTranslation } from "react-i18next";

export default function Drive() {
  const { user } = useAuth();
  const { t } = useTranslation();
  const isEmployee = isDriveEmployee(user);
  const [viewing, setViewing] = useState(null);

  const { data: employees = [] } = useQuery({
    queryKey: ["drive-employees"],
    queryFn: async () => (await api.get("/drive/employees")).data,
    enabled: !!(user?.is_super_admin || user?.is_admin),
  });

  if (!isEmployee) {
    return (
      <div className="flex flex-col items-center gap-3 py-24 text-center" data-testid="drive-forbidden">
        <ShieldAlert className="h-10 w-10 text-slate-300" />
        <h1 className="font-display text-xl font-bold">{t("drive.restrictedTitle")}</h1>
        <p className="max-w-md text-sm text-slate-500">{t("drive.restrictedBody")}</p>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="drive-page">
      <div>
        <h1 className="flex items-center gap-2 font-display text-2xl font-bold tracking-tight">
          <HardDrive className="h-6 w-6 text-sky-600" /> Pruva Drive
        </h1>
        <p className="text-sm text-slate-500">{t("drive.subtitle")}</p>
      </div>

      <Tabs defaultValue="my">
        <TabsList data-testid="drive-tabs">
          <TabsTrigger value="my" data-testid="drive-tab-my">{t("drive.tabMy")}</TabsTrigger>
          <TabsTrigger value="shared" data-testid="drive-tab-shared">{t("drive.tabShared")}</TabsTrigger>
          <TabsTrigger value="links" data-testid="drive-tab-links">{t("drive.tabLinks")}</TabsTrigger>
          {(user?.is_super_admin || user?.is_admin) && <TabsTrigger value="employees" data-testid="drive-tab-employees">{t("drive.tabEmployees")}</TabsTrigger>}
        </TabsList>

        <TabsContent value="my" className="mt-4">
          <DriveExplorer scope="personal" />
        </TabsContent>
        <TabsContent value="shared" className="mt-4">
          <DriveExplorer scope="shared" />
        </TabsContent>
        <TabsContent value="links" className="mt-4">
          <SharedLinks />
        </TabsContent>

        {(user?.is_super_admin || user?.is_admin) && (
          <TabsContent value="employees" className="mt-4">
            {viewing ? (
              <div className="space-y-3">
                <Button variant="outline" size="sm" onClick={() => setViewing(null)} data-testid="drive-employees-back">
                  <ArrowLeft className="mr-1.5 h-4 w-4" /> {t("drive.allEmployees")}
                </Button>
                <DriveExplorer scope="personal" ownerId={viewing.id} ownerLabel={t("drive.driveOf", { name: viewing.name || viewing.email })} />
              </div>
            ) : (
              <Card>
                <CardContent className="grid gap-2 p-4 sm:grid-cols-2 lg:grid-cols-3" data-testid="drive-employees-list">
                  {employees.length === 0 && <p className="col-span-full py-6 text-center text-sm text-slate-500">{t("drive.noEmployees")}</p>}
                  {employees.map((e) => (
                    <button key={e.id} onClick={() => setViewing(e)} className="flex items-center gap-3 rounded-lg border p-3 text-left transition-colors hover:bg-slate-50" data-testid={`drive-employee-${e.id}`}>
                      <UserAvatar user={e} />
                      <div className="min-w-0">
                        <div className="truncate text-sm font-semibold text-slate-900">{e.name || e.email}</div>
                        <div className="truncate font-mono text-xs text-slate-500">{e.email}</div>
                      </div>
                      <span className="ml-auto shrink-0 font-mono text-xs text-slate-400">{t("drive.filesCount", { count: e.file_count })}</span>
                    </button>
                  ))}
                </CardContent>
              </Card>
            )}
          </TabsContent>
        )}
      </Tabs>
    </div>
  );
}
